import { useEffect, useState } from "react";
import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import heroImage from "@/assets/hero-blog.jpg";
import blogPost1 from "@/assets/blog-post-1.jpg";
import blogPost2 from "@/assets/blog-post-2.jpg";
import blogPost3 from "@/assets/blog-post-3.jpg";
import blogPost4 from "@/assets/blog-post-4.jpg";
import { ArrowRight, Calendar, Clock, X, Share2, Twitter, Facebook, Linkedin, LinkIcon, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Link, useSearchParams } from "react-router-dom";
import BlogPostLink from "@/components/BlogPostLink";
import { format, isAfter, isBefore } from "date-fns";
import { toast } from "sonner";
import { z } from "zod";
import Footer from "@/components/Footer";
import { SearchFilters, FilterState } from "@/components/SearchFilters";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const emailSchema = z.string().trim().email({ message: "Invalid email address" }).max(255);

const Home = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const searchQuery = searchParams.get('search')?.toLowerCase() || '';
  const [blogPosts, setBlogPosts] = useState<any[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    categories: [],
    readTimes: [],
  });
  const [copiedPostId, setCopiedPostId] = useState<string | null>(null);

  // Function to highlight matching text
  const highlightText = (text: string, query: string) => {
    if (!query) return text;
    
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, index) => 
      part.toLowerCase() === query.toLowerCase() ? (
        <mark key={index} className="bg-primary/20 text-foreground font-medium rounded px-0.5">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  const handleClearSearch = () => {
    navigate('/');
  };

  const trackShare = async (postId: string, platform: 'twitter' | 'facebook' | 'linkedin' | 'copy' | 'native') => {
    try {
      await supabase.from('article_shares').insert({
        blog_post_id: postId,
        platform,
        user_agent: navigator.userAgent,
        referrer: document.referrer || null,
      });
    } catch (error) {
      console.error('Error tracking share:', error);
    }
  };

  const handleShare = async (post: any, platform: 'twitter' | 'facebook' | 'linkedin' | 'copy' | 'native') => {
    const shareUrl = `${window.location.origin}/blog/${post.slug}`;
    const shareTitle = post.title;
    const shareText = post.excerpt;

    if (platform === 'copy') {
      try {
        await navigator.clipboard.writeText(shareUrl);
        trackShare(post.id, platform).catch(err => console.error('Error tracking share:', err));
        
        setCopiedPostId(post.id);
        toast.success("Link copied to clipboard!");
        setTimeout(() => setCopiedPostId(null), 2000);
      } catch (err) {
        console.error('Clipboard error:', err);
        toast.error("Failed to copy link. Please try again.");
      }
      return;
    }

    await trackShare(post.id, platform);

    if (platform === 'native' && navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        console.error('Share error:', err);
      }
      return;
    }

    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    };

    if (platform in shareUrls) {
      window.open(shareUrls[platform as keyof typeof shareUrls], '_blank', 'noopener,noreferrer');
    }
  };

  const parseReadTime = (readTime: string): number => {
    const match = readTime.match(/(\d+)/);
    return match ? parseInt(match[1]) : 5;
  };

  const applyFilters = (posts: any[]) => {
    let filtered = [...posts];

    // Apply category filter
    if (filters.categories.length > 0) {
      filtered = filtered.filter((post) =>
        filters.categories.includes(post.category_id)
      );
    }

    // Apply date range filter
    if (filters.dateFrom || filters.dateTo) {
      filtered = filtered.filter((post) => {
        if (!post.published_at) return false;
        const postDate = new Date(post.published_at);
        
        if (filters.dateFrom && isBefore(postDate, filters.dateFrom)) {
          return false;
        }
        if (filters.dateTo && isAfter(postDate, filters.dateTo)) {
          return false;
        }
        return true;
      });
    }

    // Apply read time filter
    if (filters.readTimes.length > 0) {
      filtered = filtered.filter((post) => {
        const readMinutes = parseReadTime(post.read_time);
        
        return filters.readTimes.some((filterValue) => {
          if (filterValue === "quick") return readMinutes < 5;
          if (filterValue === "medium") return readMinutes >= 5 && readMinutes <= 10;
          if (filterValue === "long") return readMinutes > 10;
          return false;
        });
      });
    }

    setFilteredPosts(filtered);
  };

  useEffect(() => {
    applyFilters(blogPosts);
  }, [filters, blogPosts]);

  // Fallback images mapping
  const imageMap: Record<string, string> = {
    'getting-started-modern-web-development': blogPost1,
    'design-systems-that-scale': blogPost2,
    'future-of-ai-technology': blogPost3,
    'pinterest-affiliate-marketing': blogPost4,
  };

  useEffect(() => {
  const fetchPosts = async () => {
    console.log('Fetching blog posts...');
    try {
      let query = supabase
        .from('blog_posts')
        .select(`
          id,
          title,
          slug,
          excerpt,
          read_time,
          published_at,
          featured_image,
          category_id,
          categories (
            id,
            name,
            color
          )
        `)
        .eq('status', 'published')
        .order('published_at', { ascending: false });

      const { data, error } = await query;

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      console.log('Raw data from Supabase:', data);

      let formattedPosts = data?.map(post => ({
        id: post.id,
        slug: post.slug,
        title: post.title,
        excerpt: post.excerpt,
        category: post.categories?.name || 'Uncategorized',
        category_id: post.category_id,
        date: post.published_at ? format(new Date(post.published_at), 'MMM d, yyyy') : '',
        published_at: post.published_at,
        read_time: post.read_time || '5 min read',
        image: post.featured_image || imageMap[post.slug] || blogPost1,
      })) || [];

      // Filter by search query if present
      if (searchQuery) {
        formattedPosts = formattedPosts.filter(post =>
          post.title.toLowerCase().includes(searchQuery) ||
          post.excerpt.toLowerCase().includes(searchQuery) ||
          post.category.toLowerCase().includes(searchQuery)
        );
      }

      console.log('Formatted posts:', formattedPosts);
      console.log('Setting blog posts, count:', formattedPosts.length);
      setBlogPosts(formattedPosts);
      console.log('Setting loading to false');
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
      console.log('Loading state set to false');
    }
  };

  fetchPosts();
  }, [searchQuery]);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email
    const validationResult = emailSchema.safeParse(email);
    if (!validationResult.success) {
      toast.error(validationResult.error.errors[0].message);
      return;
    }

    setSubscribing(true);
    
    try {
      const { data: newSubscription, error } = await supabase
        .from('newsletter_subscriptions')
        .insert({ email: validationResult.data })
        .select()
        .single();

      if (error) {
        if (error.code === '23505') { // Unique constraint violation
          toast.error("You're already subscribed!");
        } else {
          throw error;
        }
      } else {
        // Send welcome email
        const { error: emailError } = await supabase.functions.invoke('send-welcome-email', {
          body: { 
            email: validationResult.data, 
            unsubscribeToken: newSubscription.unsubscribe_token 
          }
        });

        if (emailError) {
          console.error('Error sending welcome email:', emailError);
          // Don't show error to user, subscription was still successful
        }

        toast.success("Successfully subscribed! Check your inbox for a welcome email.");
        setEmail("");
      }
    } catch (error) {
      console.error('Error subscribing:', error);
      toast.error("Failed to subscribe. Please try again.");
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url(${heroImage})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/20" />
        
        <div className="container relative mx-auto px-4 py-24 md:py-32">
          <div className="mx-auto max-w-3xl text-center">
            <Badge className="mb-4">Welcome to TechBlog</Badge>
            <h1 className="mb-6 text-4xl font-bold tracking-tight md:text-6xl">
              Insights & Stories from the Tech World
            </h1>
            <p className="mb-8 text-lg text-muted-foreground md:text-xl">
              Discover the latest trends, tutorials, and insights in web development, design, and technology.
            </p>
            <div className="flex flex-col gap-4 sm:flex-row sm:justify-center">
              <Button 
                size="lg" 
                className="gap-2"
                onClick={() => document.getElementById('articles-section')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Explore Articles <ArrowRight className="h-4 w-4" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => document.getElementById('newsletter-section')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Subscribe for Updates
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Posts Section */}
      <section id="articles-section" className="container mx-auto px-4 py-16 md:py-24">
        <div className="mb-12">
          <h2 className="mb-4 text-3xl font-bold">Latest Articles</h2>
          <p className="text-muted-foreground">
            Stay up to date with the latest insights and tutorials
          </p>
        </div>

        {/* Search Results Header */}
        {searchQuery && !loading && (
          <div className="mb-8 flex items-center justify-between gap-4 rounded-lg border border-border bg-muted/50 px-4 py-3">
            <p className="text-sm font-medium">
              {filteredPosts.length === 0 ? (
                <>No results found for <span className="text-primary">"{searchParams.get('search')}"</span></>
              ) : (
                <>
                  <span className="text-primary">{filteredPosts.length}</span> {filteredPosts.length === 1 ? 'result' : 'results'} found for{' '}
                  <span className="text-primary">"{searchParams.get('search')}"</span>
                </>
              )}
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearSearch}
              className="gap-2"
            >
              Clear search <X className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Advanced Filters */}
        <SearchFilters onFilterChange={setFilters} />

        {loading ? (
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="overflow-hidden">
                <Skeleton className="h-48 w-full" />
                <CardHeader>
                  <div className="mb-2 flex items-center gap-2">
                    <Skeleton className="h-5 w-20" />
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-4 w-20" />
                  </div>
                  <Skeleton className="h-6 w-full mb-2" />
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-full mt-4" />
                  <Skeleton className="h-4 w-full mt-2" />
                </CardHeader>
              </Card>
            ))}
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No articles match the selected filters.</p>
          </div>
        ) : (
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {filteredPosts.map((post, index) => (
              <>
                <Card key={post.id} className="group overflow-hidden transition-all hover:shadow-lg">
                  <BlogPostLink to={`/blog/${post.slug}`}>
                    <div className="overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="h-48 w-full object-cover transition-transform group-hover:scale-105"
                      />
                    </div>
                  </BlogPostLink>
                  <CardHeader>
                    <div className="mb-2 flex items-center gap-2 text-sm text-muted-foreground">
                      <Badge variant="secondary">{post.category}</Badge>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {post.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {post.read_time}
                      </span>
                    </div>
                    <BlogPostLink to={`/blog/${post.slug}`}>
                      <CardTitle className="line-clamp-2 group-hover:text-primary transition-colors">
                        {highlightText(post.title, searchQuery)}
                      </CardTitle>
                    </BlogPostLink>
                    <CardDescription className="line-clamp-3">
                      {highlightText(post.excerpt, searchQuery)}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between gap-2">
                      <BlogPostLink to={`/blog/${post.slug}`}>
                        <Button variant="ghost" className="gap-2 p-0">
                          Read article <ArrowRight className="h-4 w-4" />
                        </Button>
                      </BlogPostLink>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="gap-2">
                            <Share2 className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => handleShare(post, 'twitter')} className="gap-2 cursor-pointer">
                            <Twitter className="h-4 w-4" />
                            Share on Twitter
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleShare(post, 'facebook')} className="gap-2 cursor-pointer">
                            <Facebook className="h-4 w-4" />
                            Share on Facebook
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleShare(post, 'linkedin')} className="gap-2 cursor-pointer">
                            <Linkedin className="h-4 w-4" />
                            Share on LinkedIn
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleShare(post, 'copy')} className="gap-2 cursor-pointer">
                            {copiedPostId === post.id ? <Check className="h-4 w-4" /> : <LinkIcon className="h-4 w-4" />}
                            {copiedPostId === post.id ? 'Copied!' : 'Copy Link'}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardContent>
                </Card>
              </>
            ))}
          </div>
        )}

        <div className="mt-12 text-center">
          <Button 
            size="lg" 
            variant="outline"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            View All Articles
          </Button>
        </div>
      </section>

      {/* Newsletter Section */}
      <section id="newsletter-section" className="border-t border-border bg-muted/50">
        <div className="container mx-auto px-4 py-16">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="mb-4 text-3xl font-bold">Stay in the Loop</h2>
            <p className="mb-8 text-muted-foreground">
              Subscribe to our newsletter and get the latest articles delivered directly to your inbox.
            </p>
            <form onSubmit={handleSubscribe} className="flex flex-col gap-4 sm:flex-row sm:justify-center">
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={subscribing}
                className="rounded-lg border border-input bg-background px-4 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring sm:w-80 disabled:opacity-50"
              />
              <Button type="submit" disabled={subscribing}>
                {subscribing ? "Subscribing..." : "Subscribe"}
              </Button>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
